import React from 'react'
import { FormattedMessage } from 'react-intl'
import { Button, FormGroup } from 'reactstrap'
import { X } from 'react-feather'

import EditableRow from '~/components/EditableRow'
import { Show } from '~/components/commons'
import useFormikState from '~/hooks/useFormikState'
import { FixedSizeList as List } from 'react-window'
import { createFilter } from 'react-select'
import Select from '~/components/Select'
import _ from 'lodash'

function MenuList({ options, children, maxHeight, getValue }) {
  const height = 35
  const [value] = getValue()
  const initialOffset = options.indexOf(value) * height

  return (
    <List
      height={maxHeight}
      itemCount={children.length}
      itemSize={height}
      initialScrollOffset={initialOffset}
    >
      {({ index, style }) => <div style={style}>{children[index]}</div>}
    </List>
  )
}

export const AdditionalCategoryInput = React.memo(
  ({ id = 1, value, onChange, onRemove, options, disabled }) => {
    return (
      <div className="d-flex mt-1">
        <div className="w-100">
          <label htmlFor={`additionalCategory-${id}`} style={{ opacity: 0.6 }}>
            <FormattedMessage id="Additional category" />:
          </label>
          <Select
            id={`additionalCategory-${id}`}
            className="form-control p-0 border-0"
            options={options}
            placeholder=""
            onChange={onChange}
            value={value}
            filterOption={createFilter({ ignoreAccents: false })}
            isDisabled={disabled}
            components={{ MenuList }}
            menuPortalTarget={document.querySelector('body')}
          />
        </div>
        <Show condition={!disabled && onRemove}>
          <Button
            style={{
              padding: '0',
              height: 25,
              alignSelf: 'flex-end',
              marginBottom: 5,
            }}
            color="link"
            onClick={onRemove}
            disabled={disabled}
            type="button"
          >
            <X size={25} color="#8D8D8D" />
          </Button>
        </Show>
      </div>
    )
  }
)

function PrimaryAdditionalCategories({
  controller,
  label,
  onSwitchChange,
  editableFields,
  options,
  ...props
}) {
  const primaryName = 'categories.primaryCategory'
  const aditionalName = 'categories.additionalCategories'
  const switchId = `${primaryName}Switch`

  const [primaryCategory, setPrimaryCategory] = useFormikState(
    controller,
    primaryName
  )

  const [additionalCategories, setAdditionalCategories] = useFormikState(
    controller,
    aditionalName
  )

  function handleRemoveAdditionalCategory(id) {
    setAdditionalCategories(
      additionalCategories.filter((_, index) => index !== id)
    )
  }

  const handleAdditionalCategory = (id, value) => {
    const newValues = [...additionalCategories]
    newValues[id] = value
    setAdditionalCategories(newValues)
  }

  const getValueByCategory = React.useCallback(category => {
    if (category?.value) return category.value
    if (category) {
      return {
        label: category.displayName,
        value: category,
      }
    }
    return category
  }, [])

  return (
    <>
      <EditableRow
        switchId={switchId}
        onChange={id => {
          if (editableFields?.[switchId]) {
            const primaryCategoryInitialValues = _.get(
              controller.initialValues,
              primaryName,
              ''
            )
            const additionalCategoriesInitialValues = _.get(
              controller.initialValues,
              aditionalName,
              []
            )

            setPrimaryCategory(primaryCategoryInitialValues)
            setAdditionalCategories(additionalCategoriesInitialValues)
          }
          onSwitchChange(id)
        }}
        checkboxStyle={{
          alignSelf: 'start',
          marginTop: '27px',
        }}
        isChecked={!!editableFields?.[switchId]}
      >
        <FormGroup>
          <label htmlFor={primaryName}>
            <FormattedMessage id={label} />:
          </label>
          <Select
            id={primaryName}
            options={options}
            components={{ MenuList }}
            filterOption={createFilter({ ignoreAccents: false })}
            placeholder=""
            name={primaryName}
            isDisabled={!editableFields?.[switchId]}
            value={getValueByCategory(primaryCategory)}
            onChange={({ value }) => {
              if (primaryCategory?.name) {
                setPrimaryCategory(prev => ({ ...prev, ...value }))
              } else {
                setPrimaryCategory(value)
              }
            }}
            menuPortalTarget={document.querySelector('body')}
            {...props}
          />
          <Show
            condition={
              additionalCategories?.length >= 1 && editableFields?.[switchId]
            }
          >
            <AdditionalCategoryInput
              id={0}
              value={getValueByCategory(additionalCategories?.[0])}
              onChange={({ value }) => handleAdditionalCategory(0, value)}
              onRemove={() => handleRemoveAdditionalCategory(0)}
              options={options}
              disabled={!editableFields?.[switchId]}
            />
          </Show>
          <Show
            condition={
              additionalCategories?.length >= 2 && editableFields?.[switchId]
            }
          >
            <AdditionalCategoryInput
              id={1}
              value={getValueByCategory(additionalCategories?.[1])}
              onChange={({ value }) => handleAdditionalCategory(1, value)}
              onRemove={() => handleRemoveAdditionalCategory(1)}
              disabled={!editableFields?.[switchId]}
              options={options}
            />
          </Show>
          <Show
            condition={
              additionalCategories?.length >= 3 && editableFields?.[switchId]
            }
          >
            <AdditionalCategoryInput
              id={2}
              value={getValueByCategory(additionalCategories?.[2])}
              onChange={({ value }) => handleAdditionalCategory(2, value)}
              onRemove={() => handleRemoveAdditionalCategory(2)}
              disabled={!editableFields?.[switchId]}
              options={options}
            />
          </Show>
          <Show
            condition={
              additionalCategories?.length >= 4 && editableFields?.[switchId]
            }
          >
            <AdditionalCategoryInput
              id={3}
              value={getValueByCategory(additionalCategories?.[3])}
              onChange={({ value }) => handleAdditionalCategory(3, value)}
              onRemove={() => handleRemoveAdditionalCategory(3)}
              disabled={!editableFields?.[switchId]}
              options={options}
            />
          </Show>
          <Show
            condition={
              !!editableFields?.[switchId] &&
              (additionalCategories?.length || 0) < 4
            }
          >
            <Button
              onClick={() => {
                if (additionalCategories) {
                  setAdditionalCategories([...additionalCategories, ''])
                } else {
                  setAdditionalCategories([''])
                }
              }}
              style={{ padding: '10px 0', fontSize: 14 }}
              color="link"
              type="button"
            >
              <FormattedMessage id="Add another category" />
            </Button>
          </Show>
        </FormGroup>
      </EditableRow>
    </>
  )
}

export default React.memo(PrimaryAdditionalCategories)
